<script lang="ts">
  import { onMount } from "svelte"
  import { ListPlus } from "lucide-svelte"
  import Plyr from "plyr"
  import { toast, xhr } from "$app/utils"
  import Dropdown from "$app/components/Dropdown.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import { appStore } from "$app/store.svelte"

  interface Props {
    library_video: LibraryVideoType
    onReloadPlaylist?: (playlist_id: number) => void
  }
  let props = $props()
  let { library_video, onReloadPlaylist = null }: Props = props

  let plyr_config = { title: library_video.title }

  onMount(() => {
    const player = Plyr.setup(`#player${library_video.id}`, plyr_config)
    loadPlaylists()
  })

  let openPlaylistDropdown = $state(false)

  let playlists = $state([])
  let isLoading = false
  async function loadPlaylists(): Promise<void> {
    isLoading = true
    try {
      const resp = await xhr.get(`/plans/${appStore.plan.id}/playlists.json`)

      playlists = resp.data

      playlists = findSelectedVideos(playlists)
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the playlists", "error")
    } finally {
      isLoading = false
    }
  }

  function findSelectedVideos(playlists) {
    return playlists.map((playlist) => {
      let idx = playlist.playlist_library_videos.findIndex(
        (b) => b.library_video_id === library_video.id
      )
      if (idx >= 0) {
        return {
          ...playlist,
          _selected: true,
          _playlist_library_video_id: playlist.playlist_library_videos[idx].id
        }
      } else {
        return {
          ...playlist,
          _selected: false,
          _playlist_library_video_id: null
        }
      }
    })
  }

  async function addToPlaylist(playlist_id) {
    try {
      const resp = await xhr.patch(
        `/plans/${appStore.plan.id}/playlist_library_videos/${playlist_id}`,
        {
          library_video_id: library_video.id
        }
      )

      let idx = playlists.findIndex((b) => b.id === playlist_id)

      playlists[idx] = resp.data
      playlists = findSelectedVideos(playlists)
      if (onReloadPlaylist) {
        onReloadPlaylist(playlist_id)
      }
    } catch (e) {
      toast("There was an error adding to the playlist", "error")

      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
    }
  }

  async function deleteFromPlaylist(playlist) {
    try {
      const resp = await xhr.delete(
        `/plans/${appStore.plan.id}/playlist_library_videos/${playlist._playlist_library_video_id}`
      )

      let idx = playlists.findIndex((b) => b.id === playlist.id)

      playlists[idx] = resp.data
      playlists = findSelectedVideos(playlists)
      if (onReloadPlaylist) {
        onReloadPlaylist(playlist.id)
      }
    } catch (e) {
      toast("There was an error deleting the reading", "error")
      console.error(e)
    }
  }

  function togglePlaylist(playlist) {
    if (playlist._selected) {
      deleteFromPlaylist(playlist)
    } else {
      addToPlaylist(playlist.id)
    }
  }
</script>

<svelte:head>
  <link rel="stylesheet" href="https://cdn.plyr.io/3.7.8/plyr.css" />
</svelte:head>

<div class="py-2 px-4">
  <div
    class="text-lg md:text-2xl lg:text-3xl font-bold mb-6 text-center text-indigo-900"
  >
    {library_video.title}
  </div>

  <div class="flex items-center justify-end py-2">
    <Dropdown bind:open={openPlaylistDropdown}>
      {#snippet button()}
        <button
          class="flex items-center border border-gray-300 rounded text-gray-700 py-1 px-3"
        >
          <ListPlus />
          <span class="ml-1"> Save to Playlist </span>
        </button>
      {/snippet}
      <div>
        <div
          class="origin-top-right absolute right-0 mt-2 w-64 bg-white focus:outline-none focus:outline-none z-10 shadow-lg rounded-bl rounded-br"
        >
          <div
            class="border-b border-l border-r border-gray-200 rounded-bl rounded-br py-2 px-2"
          >
            <p class="text-center font-bold py-2">Save To Playlist</p>
            <ul
              class="w-full grid grid-cols-1 gap-y-1 divide-y-1 divide-gray-200"
            >
              {#each playlists as playlist}
                <li>
                  <label class="flex items-center overflow-hidden px-2">
                    <input
                      type="checkbox"
                      onclick={() => togglePlaylist(playlist)}
                      checked={playlist._selected}
                    />
                    <span class="ml-1 text-lg">
                      {playlist.title}
                    </span>
                  </label>
                </li>
              {/each}
            </ul>
          </div>
        </div>
      </div>
    </Dropdown>
  </div>

  {#if library_video.description.length > 0}
    <div
      class="text-base px-6 py-4 mb-6 bg-lwp-blue text-gray-100 rounded shadow"
    >
      {@html library_video.description}
    </div>
  {/if}
  <div
    class="plyr__video-embed"
    id="player{library_video.id}"
    data-plyr-config={JSON.stringify(plyr_config)}
  >
    {#if library_video.source === "youtube"}
      <iframe
        src="https://www.youtube.com/embed/{library_video.video_id}?origin=https://plyr.io&amp;iv_load_policy=3&amp;modestbranding=1&amp;playsinline=1&amp;showinfo=0&amp;rel=0&amp;enablejsapi=1"
        allowfullscreen
        allowtransparency
        title={library_video.title}
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      ></iframe>
    {:else if library_video.source === "vimeo"}
      <iframe
        src="https://player.vimeo.com/video/{library_video.video_id}?h=397aa9460c&title=0&byline=0&portrait=0"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        title={library_video.title}
        allowfullscreen
        allowtransparency
      ></iframe>
    {/if}
  </div>
</div>
